import { PushNotifications } from '@capacitor/push-notifications';
import { rootFetch, onBeforeLogout, useAuth } from '../api/auth.ts';
import { useConnection } from '../api/connection.ts';
import { nativePlatform } from './platform.ts';
import { readSecure, writeSecure } from './tokenStorage.ts';

// Push in the native apps (ADR-0010/0011). Android delivers an FCM token, iOS an APNs
// device token; STE Connect sends to each platform accordingly.

const TOKEN_KEY = 'ste.pushToken';

async function register(token: string): Promise<void> {
  const platform = nativePlatform();
  if (!platform) return;
  await rootFetch<void>('/me/push-tokens', { method: 'POST', body: JSON.stringify({ token, platform }) });
  await writeSecure(TOKEN_KEY, token);
}

/** asks for permission (once) and registers this phone after login in STE Connect */
async function enable(): Promise<void> {
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== 'granted') return;
  await PushNotifications.register();
}

export async function initPush(navigate: (path: string) => void): Promise<void> {
  if (nativePlatform() === 'android') {
    await PushNotifications.createChannel({
      id: 'ste-events',
      name: 'Meldungen der Anlage',
      description: 'Laden beendet, Störungen, §14a-Dimmung',
      importance: 4,
      lightColor: '#2F7F6F',
      vibration: true,
    });
  }

  await PushNotifications.addListener('registration', ({ value }) => {
    void register(value).catch(() => undefined);
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const siteId = (notification.data as { siteId?: unknown } | undefined)?.siteId;
    if (typeof siteId === 'string') useConnection.getState().selectSite(siteId);
    navigate('/meldungen');
  });

  // the token belongs to the account: remove it before the session ends
  onBeforeLogout(async () => {
    const token = await readSecure(TOKEN_KEY);
    if (!token) return;
    await rootFetch<void>('/me/push-tokens', { method: 'DELETE', body: JSON.stringify({ token }) }).catch(
      () => undefined,
    );
    await writeSecure(TOKEN_KEY, null);
  });

  const maybeEnable = () => {
    if (useAuth.getState().status === 'authenticated' && useConnection.getState().mode === 'cloud') {
      void enable().catch(() => undefined);
    }
  };
  useAuth.subscribe((state, previous) => {
    if (state.status !== previous.status) maybeEnable();
  });
  maybeEnable();
}
